import React, { useState, useEffect } from 'react';
import { Search, Star, Zap, Mail, MessageSquare, Database, CheckCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

interface App {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: string;
  popularity: number;
  isPopular?: boolean;
}

const mockPopularApps: App[] = [
  {
    id: 'gmail',
    name: 'Gmail',
    description: 'Send and receive emails, manage your inbox',
    icon: '📧',
    category: 'Email',
    popularity: 95,
    isPopular: true,
  },
  {
    id: 'slack',
    name: 'Slack',
    description: 'Team communication and collaboration',
    icon: '💬',
    category: 'Communication',
    popularity: 92,
    isPopular: true,
  },
  {
    id: 'notion',
    name: 'Notion',
    description: 'All-in-one workspace for notes and docs',
    icon: '📝',
    category: 'Productivity',
    popularity: 88,
    isPopular: true,
  },
  {
    id: 'github',
    name: 'GitHub',
    description: 'Code repository and version control',
    icon: '🐙',
    category: 'Development',
    popularity: 90,
    isPopular: true,
  },
  {
    id: 'google-sheets',
    name: 'Google Sheets',
    description: 'Create and edit spreadsheets',
    icon: '📊',
    category: 'Productivity',
    popularity: 85,
    isPopular: true,
  },
  {
    id: 'discord',
    name: 'Discord',
    description: 'Voice, video and text communication',
    icon: '🎮',
    category: 'Communication',
    popularity: 82,
    isPopular: true,
  },
];

const categories = [
  { id: 'all', name: 'All Apps', icon: Star },
  { id: 'email', name: 'Email', icon: Mail },
  { id: 'communication', name: 'Communication', icon: MessageSquare },
  { id: 'productivity', name: 'Productivity', icon: Zap },
  { id: 'development', name: 'Development', icon: Database },
];

export const AppDiscoveryStep: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedApps, setSelectedApps] = useState<string[]>([]);

  const { data: apps = mockPopularApps, isLoading } = useQuery({
    queryKey: ['apps', selectedCategory, searchQuery],
    queryFn: async () => {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      return mockPopularApps.filter(app => {
        const matchesCategory = selectedCategory === 'all' || 
          app.category.toLowerCase() === selectedCategory;
        const matchesSearch = !searchQuery || 
          app.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          app.description.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesCategory && matchesSearch;
      });
    },
  });

  const handleAppToggle = (appId: string) => {
    setSelectedApps(prev => 
      prev.includes(appId) 
        ? prev.filter(id => id !== appId)
        : [...prev, appId]
    );
  };

  const getCategoryIcon = (categoryId: string) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.icon || Star;
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
          Discover Apps & Integrations
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          Choose from 2,500+ apps to connect with your AI assistant. Start with a few popular ones!
        </p>
      </div>

      {/* Search and Filter */}
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search apps..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                     focus:ring-2 focus:ring-blue-500 focus:border-transparent
                     bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          {categories.map((category) => {
            const Icon = category.icon;
            return (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors
                  ${selectedCategory === category.id
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600'
                  }`}
              >
                <Icon className="w-4 h-4" />
                {category.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected Apps Summary */}
      {selectedApps.length > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
            Selected Apps ({selectedApps.length})
          </h4>
          <div className="flex flex-wrap gap-2">
            {selectedApps.map(appId => {
              const app = apps.find(a => a.id === appId);
              return app ? (
                <span
                  key={appId}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-800 
                           text-blue-800 dark:text-blue-200 rounded text-sm"
                >
                  <span>{app.icon}</span>
                  {app.name}
                </span>
              ) : null;
            })}
          </div>
        </div>
      )}

      {/* Apps Grid */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-gray-200 dark:bg-gray-700 rounded-lg h-32"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {apps.map((app) => {
              const isSelected = selectedApps.includes(app.id);
              return (
                <button
                  key={app.id}
                  onClick={() => handleAppToggle(app.id)}
                  className={`p-4 border-2 rounded-lg text-left transition-all duration-200 hover:shadow-md
                    ${isSelected 
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{app.icon}</span>
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {app.name}
                        </h4>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {app.category}
                        </span>
                      </div>
                    </div>
                    {isSelected && (
                      <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    )}
                  </div>
                  
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    {app.description}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <Star className="w-3 h-3 text-yellow-500" />
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {app.popularity}% match
                      </span>
                    </div>
                    {app.isPopular && (
                      <span className="text-xs bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300 px-2 py-1 rounded">
                        Popular
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Tips */}
      <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 dark:text-white mb-2">
          💡 Pro Tips
        </h4>
        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
          <li>• Start with 2-3 apps you use daily for the best results</li>
          <li>• You can always add more apps later from the main dashboard</li>
          <li>• Popular apps have the most reliable integrations</li>
          <li>• Don't worry about selecting everything now - focus on your essentials</li>
        </ul>
      </div>

      {/* Summary */}
      <div className="text-center space-y-2">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {selectedApps.length === 0 && "Select at least one app to continue, or skip this step."}
          {selectedApps.length === 1 && "Great start! Consider adding 1-2 more for a richer experience."}
          {selectedApps.length > 1 && `Perfect! You've selected ${selectedApps.length} apps to get started.`}
        </p>
      </div>
    </div>
  );
};